import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Grid,
  InputAdornment,
  Link,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Domain as DomainIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TenantService from '../../services/tenant.service';
import { useTranslation } from '../../hooks/useTranslation';

const TenantRegistration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    // Company Information
    name: '',
    subdomain: '',
    domain: '',
    nit: '',
    address: '',
    
    // Contact Information
    email: '',
    phone: '',
    
    // Admin User Information
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirmation: '',
  });

  const [errors, setErrors] = useState({});

  const steps = [
    t('tenantRegistration.step1'),
    t('tenantRegistration.step2'),
    t('tenantRegistration.step3'),
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (error) setError('');
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      // Validate company information
      if (!formData.name.trim()) {
        newErrors.name = t('tenantRegistration.errors.nameRequired');
      }
      if (!formData.subdomain.trim()) {
        newErrors.subdomain = t('tenantRegistration.errors.subdomainRequired');
      } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
        newErrors.subdomain = t('tenantRegistration.errors.subdomainInvalid');
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('tenantRegistration.errors.emailInvalid');
      }
    } else if (step === 1) {
      // Validate contact information
      if (!formData.email.trim()) {
        newErrors.email = t('tenantRegistration.errors.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('tenantRegistration.errors.emailInvalid');
      }
    } else if (step === 2) {
      // Validate admin user information
      if (!formData.admin_name.trim()) {
        newErrors.admin_name = t('tenantRegistration.errors.adminNameRequired');
      }
      if (!formData.admin_email.trim()) {
        newErrors.admin_email = t('tenantRegistration.errors.adminEmailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
        newErrors.admin_email = t('tenantRegistration.errors.emailInvalid');
      }
      if (!formData.admin_password) {
        newErrors.admin_password = t('tenantRegistration.errors.passwordRequired');
      } else if (formData.admin_password.length < 8) {
        newErrors.admin_password = t('tenantRegistration.errors.passwordMinLength');
      }
      if (formData.admin_password !== formData.admin_password_confirmation) {
        newErrors.admin_password_confirmation = t('tenantRegistration.errors.passwordMismatch');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(activeStep)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const registrationData = {
        name: formData.name,
        subdomain: formData.subdomain,
        domain: formData.domain || null,
        nit: formData.nit || null,
        address: formData.address || null,
        email: formData.email,
        phone: formData.phone || null,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        admin_password_confirmation: formData.admin_password_confirmation,
      };

      const response = await TenantService.register(registrationData);
      
      if (response.data) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          t('tenantRegistration.errors.registrationFailed');
      setError(errorMessage);
      
      // Set field-specific errors if provided
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('tenantRegistration.companyName')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.subdomain')}
                name="subdomain"
                value={formData.subdomain}
                onChange={handleChange}
                error={!!errors.subdomain}
                helperText={errors.subdomain || t('tenantRegistration.subdomainHint')}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DomainIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.customDomain')}
                name="domain"
                value={formData.domain}
                onChange={handleChange}
                error={!!errors.domain}
                helperText={errors.domain || t('tenantRegistration.customDomainHint')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DomainIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.nit')}
                name="nit"
                value={formData.nit}
                onChange={handleChange}
                error={!!errors.nit}
                helperText={errors.nit}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('tenantRegistration.address')}
                name="address"
                value={formData.address}
                onChange={handleChange}
                error={!!errors.address}
                helperText={errors.address}
                multiline
                rows={2}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );
      
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.phone')}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );
      
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('tenantRegistration.adminAccountTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('tenantRegistration.adminAccountDescription')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.adminName')}
                name="admin_name"
                value={formData.admin_name}
                onChange={handleChange}
                error={!!errors.admin_name}
                helperText={errors.admin_name}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.adminEmail')}
                name="admin_email"
                type="email"
                value={formData.admin_email}
                onChange={handleChange}
                error={!!errors.admin_email}
                helperText={errors.admin_email}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.password')}
                name="admin_password"
                type="password"
                value={formData.admin_password}
                onChange={handleChange}
                error={!!errors.admin_password}
                helperText={errors.admin_password || t('tenantRegistration.passwordHint')}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('tenantRegistration.confirmPassword')}
                name="admin_password_confirmation"
                type="password"
                value={formData.admin_password_confirmation}
                onChange={handleChange}
                error={!!errors.admin_password_confirmation}
                helperText={errors.admin_password_confirmation}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );
      
      default:
        return null;
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              {t('tenantRegistration.successTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {t('tenantRegistration.successMessage')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('tenantRegistration.redirecting')}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Container maxWidth="md">
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                {t('tenantRegistration.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('tenantRegistration.subtitle')}
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={activeStep === steps.length - 1 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
              {renderStepContent(activeStep)}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  {t('common.back')}
                </Button>
                <Box>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                      {loading ? t('common.registering') : t('common.register')}
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                    >
                      {t('common.next')}
                    </Button>
                  )}
                </Box>
              </Box>
            </form>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('tenantRegistration.alreadyHaveAccount')}{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  {t('common.login')}
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TenantRegistration;
